import { TFunction } from 'i18next';

import { MDMStatus } from '@/constants/mdmStatus';
import { DeviceIssueType } from '@/types/devices';
import { Asset, AssetHistory, DeviceAsset } from '@/types/assets';

export const PROPERTY_EMPTY_VALUE = '-- --';

export const MACOS = 'darwin';
export const WINDOWS = 'windows';

export const getOSProperty = (asset: Asset) => {
  const assetDeviceAttributes = asset.device;
  if (!assetDeviceAttributes || !assetDeviceAttributes.osType) {
    return PROPERTY_EMPTY_VALUE;
  }
  if (
    assetDeviceAttributes.osType === MACOS &&
    assetDeviceAttributes.reportingData?.osName &&
    assetDeviceAttributes.reportingData?.osVersion
  ) {
    return `${assetDeviceAttributes.reportingData?.osVersion} ${assetDeviceAttributes.reportingData?.osName}`;
  } else if (assetDeviceAttributes.reportingData?.osName) {
    return assetDeviceAttributes.reportingData?.osName;
  } else {
    return PROPERTY_EMPTY_VALUE;
  }
};

export const getDiskUsage = (asset: Asset, t: TFunction) => {
  const assetDeviceReportingData = asset.device?.reportingData;
  if (!assetDeviceReportingData) {
    return PROPERTY_EMPTY_VALUE;
  }
  if (
    assetDeviceReportingData.storageAvailable &&
    assetDeviceReportingData.storageCapacity &&
    assetDeviceReportingData.storageUsagePercent
  ) {
    const storageInUse =
      assetDeviceReportingData.storageCapacity -
      assetDeviceReportingData.storageAvailable;

    return t('assetDetailsContent.diskUsage.format', {
      percent: assetDeviceReportingData.storageUsagePercent.toString(),
      used: Math.round(storageInUse).toString(),
      capacity: Math.round(assetDeviceReportingData.storageCapacity).toString(),
    });
  } else {
    return PROPERTY_EMPTY_VALUE;
  }
};

export const getBatteryIcon = (asset: Asset) => {
  const assetDeviceReportingData = asset.device?.reportingData;
  if (!assetDeviceReportingData) {
    return PROPERTY_EMPTY_VALUE;
  }
  if (assetDeviceReportingData.batteryHealth) {
    switch (assetDeviceReportingData.batteryHealth.toUpperCase()) {
      case 'GOOD':
        return {
          icon: 'battery-bolt',
          intent: 'success',
        };
        break;
      case 'FAIR':
        return {
          icon: 'battery-half',
          intent: 'warning',
        };
        break;
      case 'POOR':
        return {
          icon: 'battery-half',
          intent: 'error',
        };
        break;
      case 'CHECK_BATTERY':
        return {
          icon: 'battery-exclamation',
          intent: 'error',
        };
        break;
      default:
        return {
          icon: null,
          intent: null,
        };
    }
  } else {
    return {
      icon: null,
      intent: null,
    };
  }
};

export const getDeviceIssueEmailSuccessToastMessage = (
  deviceIssueType: DeviceIssueType
) => {
  switch (deviceIssueType) {
    case DeviceIssueType.Storage:
      return 'Disk space';
      break;
    case DeviceIssueType.Encryption:
      return 'Encryption';
      break;
    case DeviceIssueType.Gatekeeper:
      return 'Gatekeeper';
      break;
    case DeviceIssueType.Firewall:
      return 'Firewall';
      break;
    case DeviceIssueType.OS:
      return 'OS update';
      break;
    default:
      return PROPERTY_EMPTY_VALUE;
  }
};

export const getSingleDeviceMDMStatus = (deviceAsset: DeviceAsset) => {
  return deviceAsset.device.mdmEnrolledAt
    ? MDMStatus.INSTALLED
    : MDMStatus.NOT_INSTALLED;
};

const RELEVANT_ACTIONS: Record<string, MDMStatus> = {
  DEVICE_WIPE_REQUESTED: MDMStatus.WIPE_REQUESTED,
  DEVICE_WIPE_DONE: MDMStatus.WIPED,
  DEVICE_WIPE_ERROR: MDMStatus.WIPE_ERROR,
  DEVICE_WIPE_TIMEOUT: MDMStatus.WIPE_TIMEOUT,
  DEVICE_LOCK_ERROR: MDMStatus.LOCK_ERROR,
  DEVICE_LOCK_TIMEOUT: MDMStatus.LOCK_TIMEOUT,
  DEVICE_UNENROLL_REQUESTED: MDMStatus.UNENROLL_REQUESTED,
  DEVICE_UNENROLL_DONE: MDMStatus.UNENROLL_DONE,
  DEVICE_UNENROLL_ERROR: MDMStatus.UNENROLL_ERROR,
};

const DEVICE_LOCK_ISSUE_TYPE = 'device_lock';

export const getAssetDeviceStatusFromAssetData = (
  assetHistoryData: AssetHistory[]
): MDMStatus | null => {
  // Find the first relevant action in the asset history
  const lastRelevantAction = assetHistoryData.find(
    historyItem =>
      !!RELEVANT_ACTIONS[historyItem.action] ||
      historyItem?.data?.issue_type === DEVICE_LOCK_ISSUE_TYPE
  );
  // If there is no relevant action we exit early
  if (!lastRelevantAction) {
    return null;
  }
  // Check to see if the action is device_lock
  if (lastRelevantAction?.data?.issue_type === DEVICE_LOCK_ISSUE_TYPE) {
    // If the action is not resolved we can assume the device is still locked
    return lastRelevantAction.action !== 'DEVICE_ISSUE_RESOLVED'
      ? MDMStatus.LOCKED
      : null;
  }

  // Return the relevant action
  return RELEVANT_ACTIONS[lastRelevantAction.action];
};

type GetDeviceStatusArgs = {
  assetHistoryData: AssetHistory[];
  deviceAsset: DeviceAsset;
};
const TERMINAL_FALLBACK_STATUSES = new Set<MDMStatus>([
  MDMStatus.WIPED,
  MDMStatus.WIPE_TIMEOUT,
  MDMStatus.WIPE_ERROR,
  MDMStatus.LOCK_TIMEOUT,
  MDMStatus.LOCK_ERROR,
  MDMStatus.UNENROLL_DONE,
  MDMStatus.UNENROLL_ERROR,
]);

export const getDeviceStatus = ({
  assetHistoryData,
  deviceAsset,
}: GetDeviceStatusArgs): MDMStatus => {
  const deviceStatusFromAssetData =
    getAssetDeviceStatusFromAssetData(assetHistoryData);

  // For terminal/failed states, fall back to the real device MDM status only
  // when the device is still enrolled — so device actions remain available for retry.
  // If not enrolled (e.g. wiped and never re-enrolled), preserve the terminal status.
  if (
    deviceStatusFromAssetData &&
    TERMINAL_FALLBACK_STATUSES.has(deviceStatusFromAssetData) &&
    getSingleDeviceMDMStatus(deviceAsset) === MDMStatus.INSTALLED
  ) {
    return MDMStatus.INSTALLED;
  }

  return deviceStatusFromAssetData || getSingleDeviceMDMStatus(deviceAsset);
};
